/**
 * Button: the single button primitive.
 * Spec: `brand-redesign-docs/04-SURFACES-AND-COMPONENTS.md` (Buttons).
 *
 * Variants: `primary` (deep teal, one per view), `secondary` (white sheet,
 * hairline), `ghost` (flat text), `danger` (coral fill), `link` (inline
 * text), and for hero panels `inverse` (white pill, teal-black ink) and
 * `glass` (white glass). Styling lives in `button.css`.
 *
 * Sizes: sm 32 / md 36 / lg 40 / xl 48. md is the default.
 *
 * State props:
 *   - `loading`  swaps the label for a spinner inside the button (not next to)
 *                and sets aria-busy.
 *   - `success`  briefly morphs to a check; caller controls when to flip back.
 *
 * Icons: `leadingIcon` / `trailingIcon` accept a lucide component, sized to
 * the button.
 */

import { Check, Loader2 } from "lucide-react";
import { forwardRef, type ButtonHTMLAttributes, type ComponentType, type ReactNode } from "react";

import "./button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link" | "inverse" | "glass";
export type ButtonSize = "sm" | "md" | "lg" | "xl";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  success?: boolean;
  fullWidth?: boolean;
  leadingIcon?: ComponentType<{ size?: number; strokeWidth?: number }>;
  trailingIcon?: ComponentType<{ size?: number; strokeWidth?: number }>;
  children?: ReactNode;
}

const ICON: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18, xl: 20 };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    success = false,
    fullWidth = false,
    leadingIcon: Lead,
    trailingIcon: Trail,
    disabled,
    children,
    className,
    type = "button",
    ...rest
  },
  ref,
) {
  const iconSize = ICON[size];
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      data-full={fullWidth || undefined}
      className={className ? `btn ${className}` : "btn"}
      {...rest}
    >
      {loading ? (
        <span className="btn__spin">
          <Loader2 size={iconSize} strokeWidth={2} />
        </span>
      ) : success ? (
        <Check size={iconSize} strokeWidth={2.25} />
      ) : (
        <>
          {Lead ? <Lead size={iconSize} strokeWidth={1.75} /> : null}
          <span>{children}</span>
          {Trail ? <Trail size={iconSize} strokeWidth={1.75} /> : null}
        </>
      )}
    </button>
  );
});
